import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Reveal: animates in when scrolled into view. Includes a safety fallback so
// content is NEVER left invisible if the in-view observer fails to fire.
export const Reveal = ({ children, delay = 0, y = 40, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (inView) setShow(true);
  }, [inView]);

  // Fallback: force-visible shortly after mount regardless of observer state.
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 700);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.7, delay: show ? delay : 0, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

// Masked line-by-line reveal (industrial locking-into-place feel)
export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="mask-reveal">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const SectionLabel = ({ index, children, mr }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3">
      <span className="font-mono2 text-safety text-sm font-bold tracking-widest">{index}</span>
      <span className="h-px w-10 bg-safety" />
      <span className="font-mono2 text-xs font-bold tracking-[0.25em] uppercase text-charcoal/60">
        {children}
      </span>
    </div>
    {mr && (
      <div className="font-deva text-sm text-safetyDark/80 font-500 mt-2 pl-[3.25rem]">{mr}</div>
    )}
  </div>
);
